"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.sendAsyncResponse = exports.sendAsyncRequest = exports.postAsyncCommand = exports.handleSyncRequest = exports.isSyncRequest = exports.isAsyncCommsObject = void 0;
/**
 * Determines if the provided object is an AsyncCommObject. The incoming object
 * must have a property "type" of "command", "request", or "response" and a
 * property "id" of type string.
 * @arg {unknown} obj Object under test
 * @returns {boolean} true if the object matches the AsyncCommObject shape, false
 * otherwise.
 */
function isAsyncCommsObject(obj) {
    if (!obj || typeof obj !== 'object' || obj === null) {
        return false;
    }
    const aco = obj;
    return ['command', 'request', 'response'].includes(aco.type) && typeof aco.id === 'string';
}
exports.isAsyncCommsObject = isAsyncCommsObject;
/**
 * Determines if the provided object is a sync request. The sync request will have a
 * type property of 'sync-request' and a random id.
 * @arg {unknown} obj the object to test
 * @returns {boolean} true if the object is a sync request, false otherwise.
 */
function isSyncRequest(obj) {
    if (!obj || typeof obj !== 'object' || obj === null) {
        return false;
    }
    const sr = obj;
    return sr.type === 'sync-request' && typeof sr.id === 'string';
}
exports.isSyncRequest = isSyncRequest;
/**
 * Handles an incoming sync request by responding to it and returning the origin of the request.
 * @arg {Window?} window The window to post the sync response to.
 * @arg {MessageEvent} event The message event that might be a sync request.
 * @returns {string?} Returns the origin of the sync request on success, null if the message is
 * not a sync request, and an empty string: "" if the message was a sync request but the window
 * was not provided.
 * */
function handleSyncRequest(window, event) {
    if (!isSyncRequest(event.data)) {
        return null;
    }
    if (!window) {
        return '';
    }
    window.postMessage({ type: 'sync-response', id: event.data.id }, event.origin);
    return event.origin;
}
exports.handleSyncRequest = handleSyncRequest;
/**
 * Sends an asynchronous command object to the window via postMessage.
 * @arg {Window} window the window to post to
 * @arg {string} origin the origin of the target window, can be '*'.
 * @arg {string} id the id of the request (not generally used)
 * @arg {string} action the action to request the listener to perform
 * @arg {unknown=} data the data payload object, if needed
 */
function postAsyncCommand(window, origin, id, action, data) {
    const command = { type: 'command', id, action, data };
    window.postMessage(command, origin);
}
exports.postAsyncCommand = postAsyncCommand;
/**
 * Sends an asynchronous request object to the window via postMessage.
 * @arg {Window} window the window to post to
 * @arg {RequestMap} requests the map to add the pending resolve statement to
 * @arg {string} origin the origin of the target window, can be '*'.
 * @arg {string} id unique id representing the request (used to match up the response)
 * @arg {string} action the action to request the listener to perform
 * @arg {unknown=} data the data payload object, if needed
 */
function sendAsyncRequest(window, requests, origin, id, action, data) {
    const request = { type: 'request', id, action, data };
    return new Promise(resolve => {
        requests.set(id, resolve);
        window.postMessage(request, origin);
    });
}
exports.sendAsyncRequest = sendAsyncRequest;
/**
 * Asynchronously sends a response object containing the payload (if any) for the request
 * received earlier.
 * @arg {Window} window The window to post to.
 * @arg {string} origin The origin of the target window, can be '*'.
 * @arg {string} id Unique id of the request this is responding to.
 * @arg {unknown=} data The data payload object, if needed.
 */
function sendAsyncResponse(window, origin, id, data) {
    const response = { type: 'response', id, data };
    window.postMessage(response, origin);
}
exports.sendAsyncResponse = sendAsyncResponse;
//# sourceMappingURL=messages.js.map